import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon2.svg';
import {
  DateRangeSelector,
  ENUM_DATE_RANGE,
  getDateRangeSelector
} from '@cokitchen/cokitchen-components';
import { EditRiderType, RiderType } from 'models/rider.model';
import BaseStats, { BaseStatsType } from 'components/base/stats-details';
import { apiService } from 'utils/api';
import OnlineStatus from 'components/base/online-status';
import EditRiderDetailsModal from 'pages/logistics/partners/edit-rider-details';
import { CoKitchenType } from 'models/cokitchen.model';
import { STORAGE_KEYS } from 'utils/constants';
import Slider, { Settings } from 'react-slick';
import { getUserInitials, getUserName } from 'utils/functions';

type RiderDetailsDashboardTypes = {
  rider: RiderType;
};

const sliderSettings: Settings = {
  dots: false,
  infinite: false,
  speed: 500,
  autoplay: false,
  variableWidth: true,
  slidesToShow: 1,
  slidesToScroll: 2,
  centerMode: false,
  swipe: true,
  draggable: true,
  arrows: true,
  prevArrow: (
    <svg width='10' height='10' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M6 0.625C8.965 0.625 11.375 3.035 11.375 6C11.375 8.965 8.965 11.375 6 11.375C3.035 11.375 0.625 8.965 0.625 6C0.625 3.035 3.035 0.625 6 0.625ZM6 10.625C8.55 10.625 10.625 8.55 10.625 6C10.625 3.45 8.55 1.375 6 1.375C3.45 1.375 1.375 3.45 1.375 6C1.375 8.55 3.45 10.625 6 10.625Z'
        fill='#858585'
      />
      <path
        d='M6.63 3.86C6.725 3.86 6.82 3.895 6.895 3.97C7.04 4.115 7.04 4.355 6.895 4.5L5.395 6L6.895 7.5C7.04 7.645 7.04 7.885 6.895 8.03C6.75 8.175 6.51 8.175 6.365 8.03L4.6 6.265C4.455 6.12 4.455 5.88 4.6 5.735L6.365 3.97C6.44 3.895 6.535 3.86 6.63 3.86Z'
        fill='#858585'
      />
    </svg>
  ),
  nextArrow: (
    <svg width='10' height='10' viewBox='0 0 12 13' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M6 11.875C3.035 11.875 0.625 9.465 0.625 6.5C0.625 3.535 3.035 1.125 6 1.125C8.965 1.125 11.375 3.535 11.375 6.5C11.375 9.465 8.965 11.875 6 11.875ZM6 1.875C3.45 1.875 1.375 3.95 1.375 6.5C1.375 9.05 3.45 11.125 6 11.125C8.55 11.125 10.625 9.05 10.625 6.5C10.625 3.95 8.55 1.875 6 1.875Z'
        fill='#858585'
      />
      <path
        d='M5.37 8.63996C5.275 8.63996 5.18 8.60496 5.105 8.52996C4.96 8.38496 4.96 8.14496 5.105 7.99996L6.605 6.49996L5.105 4.99996C4.96 4.85496 4.96 4.61496 5.105 4.46996C5.25 4.32496 5.49 4.32496 5.635 4.46996L7.4 6.23496C7.545 6.37996 7.545 6.61996 7.4 6.76496L5.635 8.52996C5.56 8.60496 5.465 8.63996 5.37 8.63996Z'
        fill='#858585'
      />
    </svg>
  )
};

const RiderDetailsDashboard: FC<RiderDetailsDashboardTypes> = ({ rider }) => {
  const [dateRange, setDateRange] = useState({
    range: ENUM_DATE_RANGE.ALL,
    date: getDateRangeSelector(ENUM_DATE_RANGE.ALL)
  });
  const [riderSummary, setRiderSummary] = useState<Omit<BaseStatsType, 'loading'>[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEditRider, setShowEditRider] = useState(false);

  const isRangeAllOrSelect = () => {
    return dateRange.range === ENUM_DATE_RANGE.SELECT || dateRange.range === ENUM_DATE_RANGE.ALL;
  };

  const loadSummaryByInterval = async () => {
    setLoading(true);
    const res = await apiService.get(
      `logistics/${rider.logistics_company_id}/riders/${rider.id}/summary/deviation`,
      { params: { interval: getInterval() }, baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY }
    );
    const response = res?.data?.data;

    if (res)
      setRiderSummary([
        {
          title: 'total earned',
          value: Math.ceil(response?.current?.total_amount).toString(),
          withNaira: true,
          type: Math.sign(response?.change?.total_amount) === -1 ? 'decrease' : 'increase',
          weeklyVal: Math.abs(response?.change?.total_amount)
        },
        {
          title: 'deductions',
          value: Math.ceil(response?.current?.deduction).toString(),
          withNaira: true,
          type: Math.sign(response?.change?.deduction) === -1 ? 'decrease' : 'increase',
          weeklyVal: Math.abs(response?.change?.deduction)
        },
        {
          title: 'total trips',
          value: response?.current?.count,
          type: Math.sign(response?.change?.count) === -1 ? 'decrease' : 'increase',
          weeklyVal: Math.abs(response?.change?.count)
        },
        {
          title: 'AVG. TIME/TRIPS',
          value: response?.current?.average_duration,
          type: Math.sign(response?.change?.average_duration) === -1 ? 'decrease' : 'increase',
          weeklyVal: Math.abs(response?.change?.average_duration)
        }
      ]);
    setLoading(false);
  };

  const getInterval = () => {
    if (dateRange.range === ENUM_DATE_RANGE.TODAY) {
      return 'TODAY';
    } else if (dateRange.range === ENUM_DATE_RANGE.THIS_WEEK) {
      return 'THIS_WEEK';
    } else if (dateRange.range === ENUM_DATE_RANGE.THIS_MONTH) {
      return 'THIS_MONTH';
    } else if (dateRange.range === ENUM_DATE_RANGE.LAST_MONTH) {
      return 'LAST_MONTH';
    } else if (dateRange.range === ENUM_DATE_RANGE.YESTERDAY) {
      return 'YESTERDAY';
    }
  };
  const loadSummary = async () => {
    setLoading(true);
    const {
      date: { endDate: end_time, startDate: start_time }
    } = dateRange;
    const res = await apiService.get(
      `logistics/${rider.logistics_company_id}/riders/${rider.id}/summary`,
      { params: { start_time, end_time }, baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY }
    );
    const response = res?.data?.data;
    if (res)
      setRiderSummary([
        {
          title: 'total earned',
          value: Math.ceil(response?.total_amount).toString(),
          withNaira: true
        },
        {
          title: 'deductions',
          value: Math.ceil(response?.deduction).toString(),
          withNaira: true
        },
        {
          title: 'total trips',
          value: response?.count
        },
        {
          title: 'AVG. TIME/TRIPS',
          value: response.average_duration
        }
      ]);
    setLoading(false);
  };

  useEffect(() => {
    if (!isRangeAllOrSelect()) {
      loadSummaryByInterval();
    } else {
      loadSummary();
    }
  }, [dateRange]);

  const cokitchen_id = localStorage.getItem(STORAGE_KEYS.COKITCHEN);
  const cokitchenArray: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) || '[]'
  );
  const activeCokitchen = cokitchenArray.find((cokitchen) => cokitchen.id === cokitchen_id);
  const activeRiderPolygons = activeCokitchen?.cokitchen_polygons?.filter((polygon) =>
    rider.cokitchen_polygon_ids?.includes(polygon.id)
  );

  return (
    <div>
      <div className=''>
        <div className='flex flex-col items-center justify-center gap-2'>
          {rider.profile_image ? (
            <img
              src={rider.profile_image ?? ''}
              className='w-[90px] h-[90px] rounded-[90px] object-cover'
            />
          ) : (
            <div className='w-[90px] h-[90px] rounded-[90px] border border-gray-2 flex items-center justify-center uppercase text-lg font-medium'>
              {getUserInitials(rider)}
            </div>
          )}

          <div className='flex gap-2 items-center'>
            <h3 className=' text-2xl font-semibold'>{getUserName(rider)}</h3>
            <EditIcon className='cursor-pointer' onClick={() => setShowEditRider(true)} />
          </div>
          <div className='flex flex-col sm:flex-row gap-2'>
            <Slider
              {...sliderSettings}
              className='max-w-[300px] flex border border-[#E6E6E6] rounded h-[26px]'>
              {activeRiderPolygons?.map((item) => (
                <div key={item.id} className='text-xs flex text-[#595959] ml-1  '>
                  {item.name},
                </div>
              ))}
            </Slider>
            <OnlineStatus small online={rider.online} />
          </div>
        </div>
        <div className='w-[110px] my-4'>
          <DateRangeSelector showTime onChange={setDateRange} defaultRange={ENUM_DATE_RANGE.ALL} />
        </div>
      </div>
      <div className='flex max-md:flex-col gap-2 justify-between md:items-start w-full bg-[#F5FBFF] p-4 rounded-[8px] border border-[#DEE1E3]'>
        {!loading ? (
          riderSummary?.map(({ title, type, time, value, weeklyVal, withNaira }, i) => (
            <div
              className={`max-md:w-full md:basis-[20%] flex justify-center ${
                riderSummary.length - 1 === i
                  ? ''
                  : 'border-b py-2 md:border-r md:border-b-0 border-gray-2'
              }`}
              key={i}>
              <BaseStats
                title={title}
                type={type}
                weeklyVal={weeklyVal}
                value={value}
                time={time}
                withNaira={withNaira}
                activeDateRange={dateRange.range}
                small
                loading={loading}
              />
            </div>
          ))
        ) : (
          <div className='leading-[69px] text-center w-full'>
            <p>Loading...</p>
          </div>
        )}
      </div>
      {showEditRider && (
        <EditRiderDetailsModal
          onClose={() => setShowEditRider(false)}
          rider={rider ?? ({} as Partial<EditRiderType>)}
          show={showEditRider}
        />
      )}
    </div>
  );
};

export default RiderDetailsDashboard;
